// import 'server-only'; // @note can uncomment after dynamicRoutes was migrated to the app router

import { CmsContentfulTypename } from '$cms/contentful.types';
import type { ContentfulTranslatedEntity } from '$cms/contentful.types';
import type { ContentComponentTranslatedEntities } from '$cms/ContentComponent';
import {
  CmsImageSchema,
  CmsLinkSchema,
  CmsNumberSchema,
  CmsRichTextSchema,
  CmsShortTextSchema,
} from '$cms/types';
import type { CmsPageSeo, CmsTranslatedEntity, CmsPageContent } from '$cms/types';

/**
 * Transforms a ContentfulTranslatedEntity to our internal CMS type.
 * @todo transform the rest of the types when we need them
 */
export const transformTranslatedEntity = (
  contentfulTranslatedEntity: ContentfulTranslatedEntity
): CmsTranslatedEntity => {
  const typename = contentfulTranslatedEntity.__typename;
  switch (typename) {
    case CmsContentfulTypename.shortText: {
      return CmsShortTextSchema.parse(contentfulTranslatedEntity);
    }
    case CmsContentfulTypename.number: {
      return CmsNumberSchema.parse(contentfulTranslatedEntity);
    }
    case CmsContentfulTypename.richText: {
      return CmsRichTextSchema.parse({
        key: contentfulTranslatedEntity.key,
        content: contentfulTranslatedEntity.content?.json,
      });
    }
    case CmsContentfulTypename.link: {
      return CmsLinkSchema.parse(contentfulTranslatedEntity);
    }
    case CmsContentfulTypename.image: {
      const {
        key,
        img: [{ original_secure_url: url, width, height }],
        alt,
      } = contentfulTranslatedEntity;
      return CmsImageSchema.parse({ key, url, width, height, alt });
    }
    default:
      throw new TypeError(`Unimplemented translated entity transform: ${typename}`);
  }
};

export function addEntityToTranslatedEntities(
  o: ContentComponentTranslatedEntities,
  entity: CmsTranslatedEntity
): ContentComponentTranslatedEntities {
  const key = entity.key.split('.')[1];
  if (!key) {
    /**
     * @note The `key` type says it's only `string` but it can be `undefined` if the key is in an unexpected format.
     */
    return o;
  }
  /**
   * key in the form `{field}{number}`.
   * eg. itemTitle1 -> { itemTitle: { 1: "title 1"} }
   */
  const matchKeyRecord = key.match(/(\w+)([0-9]+)$/);
  if (matchKeyRecord) {
    const [, k, kNo] = matchKeyRecord;
    return Object.assign(o, { [k]: Object.assign(o[k] ?? {}, { [kNo]: entity }) });
  }
  /**
   * normal key
   */
  return Object.assign(o, { [key]: entity });
}

/**
 * Prepares the translated entities data from Contentful to the format expected by ContentComponents.
 */
export const makeContentComponentTranslatedEntities = (
  contentfulTranslatedEntities: ContentfulTranslatedEntity[] | undefined
): ContentComponentTranslatedEntities => {
  const translatedEntities = contentfulTranslatedEntities?.reduce((o, contentfulEntity) => {
    const entity = transformTranslatedEntity(contentfulEntity);
    return addEntityToTranslatedEntities(o, entity);
  }, {} as ContentComponentTranslatedEntities);

  return translatedEntities ?? {};
};

export const makeContentPageTranslatedEntities = (
  contentPageData: CmsPageContent
): ContentComponentTranslatedEntities => {
  const cmsTranslatedEntities = contentPageData.components?.[0]?.translatedEntities;
  const translatedEntities = cmsTranslatedEntities?.reduce(
    (o, entity) => addEntityToTranslatedEntities(o, entity),
    {} as ContentComponentTranslatedEntities
  );

  return translatedEntities ?? {};
};

export const makeMetadata = (cmsSeo?: CmsPageSeo): CmsPageSeo => {
  return {
    noindex: cmsSeo?.noindex ?? false,
    nofollow: cmsSeo?.nofollow ?? false,
    title: cmsSeo?.title ?? 'Fixter',
    metaDescription: cmsSeo?.metaDescription ?? 'Fixter',
    metaKeywords: cmsSeo?.metaKeywords ?? [],
  };
};
